import React, { useState } from 'react';
import { ethers } from 'ethers';
import Card from '../Common/Card';
import Button from '../Common/Button';
import Input from '../Common/Input';
import { useWeb3 } from '../../context/Web3Context';
import { notify, removeNotify } from '../../utils/notify';
import { parseContractError } from '../../utils/errors';
import { shortAddress, parseUSDT } from '../../utils/format';

// OWNER-ONLY (sensitive operations). Renders are gated in AdminPanel.

const OwnerControls = ({ ownerAddr, adminAddr, adminWallet, onChange }) => {
  const { contract } = useWeb3();

  const [newAdmin, setNewAdmin] = useState('');
  const [newOwner, setNewOwner] = useState('');
  const [newAdminWallet, setNewAdminWallet] = useState('');
  const [emergencyAmt, setEmergencyAmt] = useState('');
  const [busy, setBusy] = useState('');

  const exec = async (label, fn, onDone) => {
    if (!contract) return;
    setBusy(label);
    let lid = null;
    try {
      lid = notify.loading(`${label}...`);
      const tx = await fn();
      await tx.wait();
      removeNotify(lid); lid = null;
      notify.success(`${label} successful`);
      onDone && onDone();
      onChange && onChange();
    } catch (err) {
      if (lid) removeNotify(lid);
      notify.error(parseContractError(err));
    } finally {
      setBusy('');
    }
  };

  return (
    <Card title="Ownership & Emergency" subtitle="Owner only · High-impact actions" glow>
      <div style={{
        padding: 14,
        background: 'rgba(255, 46, 99, 0.05)',
        border: '1px solid rgba(255, 46, 99, 0.25)',
        borderRadius: 'var(--radius-md)',
        marginBottom: 20,
        fontSize: 13,
        color: 'var(--text-secondary)'
      }}>
        Actions in this section transfer control or move funds. Double-check addresses before submitting.
      </div>

      <div className="grid grid-2" style={{ gap: 20 }}>
        <div style={{
          padding: 14,
          background: 'var(--bg-input)',
          border: '1px solid var(--border-secondary)',
          borderRadius: 'var(--radius-md)'
        }}>
          <div className="stat-label">Owner</div>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 13, marginTop: 4 }}>
            {ownerAddr ? shortAddress(ownerAddr) : '-'}
          </div>
        </div>
        <div style={{
          padding: 14,
          background: 'var(--bg-input)',
          border: '1px solid var(--border-secondary)',
          borderRadius: 'var(--radius-md)'
        }}>
          <div className="stat-label">Admin</div>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 13, marginTop: 4 }}>
            {adminAddr ? shortAddress(adminAddr) : '-'}
          </div>
        </div>
        <div style={{
          padding: 14,
          background: 'var(--bg-input)',
          border: '1px solid var(--border-secondary)',
          borderRadius: 'var(--radius-md)',
          gridColumn: '1 / -1'
        }}>
          <div className="stat-label">Admin Wallet (Fund Receiver)</div>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 13, marginTop: 4 }}>
            {adminWallet ? shortAddress(adminWallet) : '-'}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 24 }} className="grid grid-2">
        <div>
          <Input
            label="Set Admin"
            value={newAdmin}
            onChange={(e) => setNewAdmin(e.target.value)}
            placeholder="0x..."
          />
          <Button
            variant="primary"
            size="sm"
            block
            loading={busy === 'set-admin'}
            disabled={!ethers.utils.isAddress(newAdmin)}
            onClick={() => exec('set-admin', () => contract.setAdmin(newAdmin), () => setNewAdmin(''))}
          >Update Admin</Button>
        </div>

        <div>
          <Input
            label="Admin Wallet (Fund Receiver)"
            value={newAdminWallet}
            onChange={(e) => setNewAdminWallet(e.target.value)}
            placeholder="0x..."
          />
          <Button
            variant="primary"
            size="sm"
            block
            loading={busy === 'admin-wallet'}
            disabled={!ethers.utils.isAddress(newAdminWallet)}
            onClick={() => exec('admin-wallet', () => contract.updateAdminWallet(newAdminWallet), () => setNewAdminWallet(''))}
          >Update Admin Wallet</Button>
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <Input
            label="Transfer Ownership"
            value={newOwner}
            onChange={(e) => setNewOwner(e.target.value)}
            placeholder="0x..."
            helper="Irreversible — verify the address twice"
          />
          <Button
            variant="danger"
            size="sm"
            block
            loading={busy === 'transfer'}
            disabled={!ethers.utils.isAddress(newOwner)}
            onClick={() => exec('transfer', () => contract.transferOwnership(newOwner), () => setNewOwner(''))}
          >Transfer Ownership</Button>
        </div>

        <div style={{ gridColumn: '1 / -1', paddingTop: 16, borderTop: '1px solid var(--border-secondary)' }}>
          <Input
            label="Emergency Withdraw (amount)"
            value={emergencyAmt}
            onChange={(e) => setEmergencyAmt(e.target.value.replace(/[^0-9.]/g, ''))}
            placeholder="0.00"
            suffix="USDT"
            helper="Sends amount to owner. Use 'Withdraw All' to drain the contract."
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="ghost"
              size="sm"
              loading={busy === 'em-amt'}
              disabled={!emergencyAmt}
              onClick={() => exec('em-amt', () => contract.emergencyWithdrawAmount(parseUSDT(emergencyAmt)), () => setEmergencyAmt(''))}
            >Withdraw Amount</Button>
            <Button
              variant="danger"
              size="sm"
              loading={busy === 'em-all'}
              onClick={() => exec('em-all', () => contract.emergencyWithdraw())}
            >Withdraw All</Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default OwnerControls;
